import type { AvailabilitySlot, MinuteRange } from '@reditelji/shared';
import { create } from 'zustand';

export type BoardView = 'mesta' | 'ljudje';
export type AsideTab = 'predlog' | 'zivo' | 'opozorila' | 'prostovoljci' | 'podrobnosti';
export type FilterChip = 'nujni' | 'vrzeli' | 'caka' | 'brifing';

export interface GapSelection {
  postId: string;
  fromMin: number;
  toMin: number;
  /**
   * Set when the selection is an open shift row rather than a bare hole in the
   * post's window. The row has details of its own — times, meeting point, note
   * and the assignee picker — kept behind the candidates on Podrobnosti.
   */
  shiftId?: string;
}

/**
 * Somebody the side panel is about to place, drawn on the board before it
 * happens: the highlighted candidate of a gap, or the person just picked in a
 * shift's `Oseba`. `tone` is their candidate group.
 */
/** A group of the candidates for a gap, which can be folded. */
export type FillGroup = 'rec' | 'warn' | 'flag' | 'busy' | 'teams';

export interface AssignPreview extends MinuteRange {
  postId: string;
  volunteerId: string;
  name: string;
  tone: 'rec' | 'warn' | 'flag' | 'team';
}

/**
 * What the Pregled heatmap shows (SPEC §1.1A). Kept here rather than in the
 * card, so a trip to Časovnica and back finds it as it was left.
 */
export interface HeatView {
  /** Lens: posts nobody is coming to (a gap or a rejection). */
  short: boolean;
  /** Lens: posts waiting for a confirmation. */
  pending: boolean;
  urgentOnly: boolean;
  hideQuiet: boolean;
  /** Open day bands by event-day id; null until the card first seeds it. */
  days: Record<string, boolean> | null;
  /** Open sectors, keyed `dayId:sectorId`. */
  sectors: Record<string, boolean>;
}

/**
 * A resize waiting on the coordinator's word, because committing it would put
 * somebody who has already confirmed — or already been briefed — back into the
 * queue, or would stretch their shift into hours they marked "ni na voljo". A
 * version bump cannot be undone (there is no decrement anywhere), and what a
 * volunteer said about their own day is not to be overruled by accident, so a
 * gesture this easy to fire has to ask first.
 */
export interface PendingResize {
  shiftId: string;
  updatedAt: string;
  postCode: string;
  name: string;
  fromMin: number;
  toMin: number;
  wasFromMin: number;
  wasToMin: number;
  /** What the change costs the person; null when only `closed` asks. */
  reason: 'potrjen' | 'brifing' | null;
  /** Slots the new range runs into that the person marked "ni na voljo". */
  closed: AvailabilitySlot[];
}

export interface UndoState {
  head: string;
  sub: string;
  run: () => void;
  /** A way forward beside the undo — "Poišči zamenjavo" after a "Ne pride". */
  alt?: { label: string; run: () => void };
}

interface UiState {
  selectedDayId: string | null;
  setSelectedDay: (id: string) => void;

  /**
   * Silent-edit override: "popravek brez ponovne potrditve" (SPEC §1.1B).
   * null = follow the default (on in live mode, off otherwise).
   */
  silentOverride: boolean | null;
  setSilentOverride: (on: boolean | null) => void;

  /** Live-mode override: null = auto (on when the selected day is today). */
  liveOverride: boolean | null;
  setLiveOverride: (on: boolean | null) => void;

  /** Planning footer expanded while in live mode. */
  planOpen: boolean;
  setPlanOpen: (open: boolean) => void;

  /** Bumped to ask the board to scroll to the current time ("zdaj HH:MM"). */
  nowSignal: number;
  pingNow: () => void;

  /**
   * One request for the board to centre a minute of a day, spent by the board.
   * With a `postId` the board also scrolls that row into view.
   */
  timelineReveal: { dayId: string; min: number; postId?: string } | null;
  revealTimelineAt: (dayId: string, min: number, postId?: string) => void;
  clearTimelineReveal: () => void;

  heat: HeatView;
  setHeat: (patch: Partial<HeatView>) => void;

  boardView: BoardView;
  setBoardView: (v: BoardView) => void;

  filters: Set<FilterChip>;
  toggleFilter: (f: FilterChip) => void;

  sectorFocus: string | null;
  setSectorFocus: (name: string | null) => void;

  collapsedGroups: Record<string, boolean>;
  toggleGroup: (key: string) => void;

  hoveredVolunteerId: string | null;
  setHoveredVolunteer: (id: string | null) => void;

  /**
   * The person the board is looking through (SPEC §1.1B): picked on the
   * Prostovoljci tab, so leaving that tab or selecting a shift or a gap ends
   * it. A day switch keeps it — a person spans days. Whether it applies right
   * now (a past day, a plan up, somebody since marked not coming) is decided
   * where it is drawn, never stored here.
   */
  lensVolunteerId: string | null;
  setLens: (id: string | null) => void;

  asideTab: AsideTab;
  setAsideTab: (t: AsideTab) => void;

  /** The proposal the keyboard walk is standing on, by gap key. */
  focusedProposal: string | null;
  focusProposal: (key: string | null) => void;

  /** Plan mode: show only the rows the plan touches. */
  planFocusOnly: boolean;
  setPlanFocusOnly: (on: boolean) => void;

  selectedShiftId: string | null;
  selectShift: (id: string | null) => void;

  selectedGap: GapSelection | null;
  selectGap: (gap: GapSelection | null) => void;

  /** The part of the selected gap being filled, when narrower than the gap. */
  gapPiece: (MinuteRange & { postId: string }) | null;
  setGapPiece: (piece: (MinuteRange & { postId: string }) | null) => void;

  assignPreview: AssignPreview | null;
  setAssignPreview: (preview: AssignPreview | null) => void;

  /**
   * Candidate groups the coordinator folded or opened, kept while they move
   * from gap to gap. A group nobody touched follows the default: the first
   * group with anyone in it is open, the rest folded.
   */
  fillGroupsOpen: Partial<Record<FillGroup, boolean>>;
  setFillGroupOpen: (group: FillGroup, open: boolean) => void;

  /**
   * Nothing selected, on whichever tab is open: Podrobnosti reads empty, and a
   * drag from Prostovoljci stays on the pool it came from.
   */
  clearSelection: () => void;

  pendingResize: PendingResize | null;
  setPendingResize: (r: PendingResize | null) => void;

  changelogOpen: boolean;
  setChangelogOpen: (open: boolean) => void;

  poolSearch: string;
  setPoolSearch: (q: string) => void;

  /** Session-memory undo (SPEC §4.10): window edits + last assignment. */
  undo: UndoState | null;
  setUndo: (u: UndoState | null) => void;
}

export const useUi = create<UiState>((set) => ({
  selectedDayId: null,
  setSelectedDay: (id) =>
    set({
      selectedDayId: id,
      selectedShiftId: null,
      selectedGap: null,
      gapPiece: null,
      assignPreview: null,
      sectorFocus: null,
      timelineReveal: null,
    }),
  silentOverride: null,
  setSilentOverride: (on) => set({ silentOverride: on }),
  liveOverride: null,
  setLiveOverride: (on) => set({ liveOverride: on }),
  planOpen: false,
  setPlanOpen: (open) => set({ planOpen: open }),
  nowSignal: 0,
  pingNow: () => set((s) => ({ nowSignal: s.nowSignal + 1 })),
  timelineReveal: null,
  revealTimelineAt: (dayId, min, postId) => set({ timelineReveal: { dayId, min, postId } }),
  clearTimelineReveal: () => set({ timelineReveal: null }),
  heat: { short: true, pending: true, urgentOnly: false, hideQuiet: false, days: null, sectors: {} },
  setHeat: (patch) => set((s) => ({ heat: { ...s.heat, ...patch } })),
  boardView: 'mesta',
  setBoardView: (v) =>
    set({ boardView: v, selectedShiftId: null, selectedGap: null, gapPiece: null, assignPreview: null }),
  filters: new Set<FilterChip>(),
  toggleFilter: (f) =>
    set((s) => {
      const filters = new Set(s.filters);
      if (filters.has(f)) filters.delete(f);
      else filters.add(f);
      return { filters };
    }),
  sectorFocus: null,
  setSectorFocus: (name) => set({ sectorFocus: name }),
  collapsedGroups: {},
  toggleGroup: (key) =>
    set((s) => ({ collapsedGroups: { ...s.collapsedGroups, [key]: !s.collapsedGroups[key] } })),
  hoveredVolunteerId: null,
  setHoveredVolunteer: (id) => set({ hoveredVolunteerId: id }),
  asideTab: 'opozorila',
  setAsideTab: (t) => set(t === 'prostovoljci' ? { asideTab: t } : { asideTab: t, lensVolunteerId: null }),
  lensVolunteerId: null,
  setLens: (id) => set({ lensVolunteerId: id }),
  focusedProposal: null,
  focusProposal: (key) => set({ focusedProposal: key }),
  planFocusOnly: false,
  setPlanFocusOnly: (on) => set({ planFocusOnly: on }),
  selectedShiftId: null,
  selectShift: (id) =>
    set({
      selectedShiftId: id,
      selectedGap: null,
      gapPiece: null,
      assignPreview: null,
      asideTab: id ? 'podrobnosti' : 'opozorila',
      lensVolunteerId: null,
    }),
  pendingResize: null,
  setPendingResize: (r) => set({ pendingResize: r }),
  selectedGap: null,
  selectGap: (gap) =>
    set(
      gap
        ? {
            selectedGap: gap,
            selectedShiftId: gap.shiftId ?? null,
            gapPiece: null,
            assignPreview: null,
            asideTab: 'podrobnosti',
            lensVolunteerId: null,
          }
        : { selectedGap: null, gapPiece: null, assignPreview: null },
    ),
  gapPiece: null,
  setGapPiece: (piece) => set({ gapPiece: piece }),
  assignPreview: null,
  setAssignPreview: (preview) => set({ assignPreview: preview }),
  fillGroupsOpen: {},
  setFillGroupOpen: (group, open) => set((s) => ({ fillGroupsOpen: { ...s.fillGroupsOpen, [group]: open } })),
  clearSelection: () =>
    set({
      selectedShiftId: null,
      selectedGap: null,
      gapPiece: null,
      assignPreview: null,
    }),
  changelogOpen: false,
  setChangelogOpen: (open) => set({ changelogOpen: open }),
  poolSearch: '',
  setPoolSearch: (q) => set({ poolSearch: q }),
  undo: null,
  setUndo: (u) => set({ undo: u }),
}));
